/**
 * IndustryPresetModal — Pick industry → apply preset prompt vào project.systemPrompt
 *
 * Triggered từ tab Cấu hình (ProjectDetail), button "Chọn preset ngành" trên systemPrompt textarea.
 * Replace toàn bộ systemPrompt textarea (user có thể edit thêm sau).
 */

import React, { useState } from 'react';
import { X, Check, Scale, Briefcase, BarChart3, Package, Building2, FileEdit, AlertTriangle } from 'lucide-react';
import { INDUSTRY_PRESETS, type IndustryId, type IndustryPreset } from '../../utils/industryPrompts';

const ICON_MAP = {
  Legal: Scale,
  Finance: Briefcase,
  Audit: BarChart3,
  Export: Package,
  'Real Estate': Building2,
  Other: FileEdit,
} as const;

interface Props {
  isOpen: boolean;
  currentIndustry?: IndustryId | null;
  hasExistingPrompt?: boolean;
  onClose: () => void;
  onApply: (preset: IndustryPreset) => void;
}

const IndustryPresetModal: React.FC<Props> = ({
  isOpen,
  currentIndustry,
  hasExistingPrompt,
  onClose,
  onApply,
}) => {
  const [selectedId, setSelectedId] = useState<IndustryId | null>(currentIndustry || null);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const selected = INDUSTRY_PRESETS.find(p => p.id === selectedId);

  const handleApply = () => {
    if (!selected) return;
    if (hasExistingPrompt && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    onApply(selected);
    setShowConfirm(false);
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-violet-50">
          <div>
            <h3 className="text-base font-bold text-slate-800">Chọn preset theo ngành</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Thay thế "Chỉ thị hệ thống" bằng template tune sẵn cho ngành bạn
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/60 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Đóng"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Industry cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mb-4">
            {INDUSTRY_PRESETS.map(preset => {
              const Icon = ICON_MAP[preset.id];
              const isSelected = selectedId === preset.id;
              const isCurrent = currentIndustry === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => { setSelectedId(preset.id); setShowConfirm(false); }}
                  className={`
                    relative p-3 rounded-xl border-2 text-left transition-all
                    ${isSelected
                      ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100'
                      : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50'
                    }
                  `}
                >
                  {isCurrent && (
                    <span className="absolute top-1.5 right-1.5 text-[9px] font-bold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">
                      HIỆN TẠI
                    </span>
                  )}
                  <Icon className={`w-5 h-5 mb-1.5 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} />
                  <div className={`text-xs font-bold mb-0.5 ${isSelected ? 'text-indigo-900' : 'text-slate-700'}`}>
                    {preset.label}
                  </div>
                  <div className="text-[10px] text-slate-500 leading-tight">{preset.description}</div>
                </button>
              );
            })}
          </div>

          {/* Preview */}
          {selected && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Xem trước prompt
                </h4>
                <span className="text-xs text-slate-400">{selected.prompt.length} ký tự</span>
              </div>
              <pre className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono max-h-60 overflow-y-auto">
                {selected.prompt}
              </pre>
            </div>
          )}

          {/* Confirm overwrite warning */}
          {showConfirm && hasExistingPrompt && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <div className="font-bold mb-1">Ghi đè prompt hiện tại?</div>
                <p className="text-xs leading-relaxed">
                  Chỉ thị hệ thống hiện tại sẽ bị <b>thay thế toàn bộ</b> bằng preset <b>{selected?.label}</b>.
                  Nếu bạn đã custom prompt trước đó, copy paste save lại trước khi confirm.
                </p>
                <p className="text-xs leading-relaxed mt-2">
                  Bấm <b>"Áp dụng preset"</b> lần nữa để xác nhận.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
          <button
            onClick={onClose}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-4 py-2 transition-colors"
          >
            Huỷ
          </button>
          <button
            onClick={handleApply}
            disabled={!selected}
            className="
              inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
              bg-indigo-600 text-white text-sm font-bold
              shadow-lg shadow-indigo-100
              hover:bg-indigo-700 hover:shadow-xl hover:shadow-indigo-200
              active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-all
            "
          >
            <Check className="w-4 h-4" />
            {showConfirm ? 'Xác nhận ghi đè' : 'Áp dụng preset'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryPresetModal;
